/**
 * Centralized Job Manager for Multi-Job System
 *
 * This service manages all generation jobs in a coordinated manner,
 * replacing the multiple individual polling intervals with a single
 * coordinated polling loop to prevent race conditions and database contention.
 */

import { isFeatureEnabled } from '../config/featureFlags';
import { databaseQueue } from './DatabaseQueue';

// Lazy import eventBus to avoid circular dependency issues
let eventBus = null;
const getEventBus = () => {
    if (!eventBus) {
        eventBus = require('../utils/eventBus').default;
    }
    return eventBus;
};

class JobManager {
    constructor() {
        this.jobs = new Map(); // Map<string, JobState>
        this.isPolling = false;
        this.pollingInterval = null;
        this.pollingIntervalMs = 2500; // 2.5 seconds
        this.databaseQueue = databaseQueue; // Connect to database queue
        this.listeners = new Set(); // Set of callback functions for state changes
        this.listenersInitialized = false; // Track if event listeners are set up

        // Bind methods to preserve 'this' context
        this.pollAllJobs = this.pollAllJobs.bind(this);

        console.log('[JobManager] Initialized (inactive until feature flag enabled)');
    }

    /**
     * Initialize event listeners when first needed
     */
    initializeEventListeners() {
        if (this.listenersInitialized) return;

        try {
            const eventBus = getEventBus();
            if (!eventBus) {
                console.error('[JobManager] EventBus not available, cannot initialize listeners');
                return;
            }

            // ✅ Defensive event handlers with try-catch
            eventBus.on('job_add', (uid, jobData) => {
                try {
                    this.addJob(uid, jobData);
                } catch (error) {
                    console.error(`[JobManager] Error handling job_add event for ${uid}:`, error);
                }
            });

            eventBus.on('job_update', (uid, updates, expectedVersion) => {
                try {
                    this.updateJob(uid, updates, expectedVersion);
                } catch (error) {
                    console.error(`[JobManager] Error handling job_update event for ${uid}:`, error);
                }
            });

            eventBus.on('job_remove', (uid) => {
                try {
                    this.removeJob(uid);
                } catch (error) {
                    console.error(`[JobManager] Error handling job_remove event for ${uid}:`, error);
                }
            });

            eventBus.on('job_cancel', (uid) => {
                try {
                    this.cancelJob(uid);
                } catch (error) {
                    console.error(`[JobManager] Error handling job_cancel event for ${uid}:`, error);
                }
            });

            this.listenersInitialized = true;
            console.log('[JobManager] Event listeners initialized');
        } catch (error) {
            console.error('[JobManager] Error initializing event listeners:', error);
        }
    }

    /**
     * Add a new job to the manager
     * @param {string} uid - Unique job identifier
     * @param {object} jobData - Initial job data
     */
    addJob(uid, jobData = {}) {
        if (!isFeatureEnabled('USE_NEW_JOB_MANAGER')) {
            console.log('[JobManager] Feature disabled, ignoring addJob');
            return;
        }


        // Initialize event listeners if not already done
        this.initializeEventListeners();

        const jobState = {
            uid,
            status: 'generating',
            progress: 0,
            count: jobData.count || 1,
            totalImages: 0,
            error: null,
            outputs: [],
            processedUIDs: [],
            timestamp: Date.now(),
            lastProgressTime: Date.now(),
            lastProgress: 0,
            lockVersion: 1,
            ...jobData
        };

        this.jobs.set(uid, jobState);

        // Start polling if this is the first job
        if (this.jobs.size === 1) {
            this.startPolling();
        }

        this.notifyListeners('job_added', uid, jobState);
    }

    /**
     * Update an existing job's state
     * @param {string} uid - Job identifier
     * @param {object} updates - Updates to apply
     * @param {number} expectedVersion - Expected lock version for optimistic locking
     */
    updateJob(uid, updates, expectedVersion = null) {
        if (!isFeatureEnabled('USE_NEW_JOB_MANAGER')) {
            return;
        }

        const existingJob = this.jobs.get(uid);
        if (!existingJob) {
            console.warn(`[JobManager] Cannot update non-existent job ${uid}`);
            return;
        }

        // Optimistic locking check
        if (expectedVersion !== null && existingJob.lockVersion !== expectedVersion) {
            console.warn(`[JobManager] Job ${uid} version mismatch (expected: ${expectedVersion}, actual: ${existingJob.lockVersion})`);
            return;
        }

        const updatedJob = {
            ...existingJob,
            ...updates,
            lockVersion: existingJob.lockVersion + 1,
            lastUpdateTime: Date.now()
        };

        this.jobs.set(uid, updatedJob);
        console.log(`[JobManager] Updated job ${uid}:`, updates);

        this.notifyListeners('job_updated', uid, updatedJob);

        // Stop polling if no more active jobs
        if (this.getActiveJobs().length === 0) {
            this.stopPolling();
        }
    }

    /**
     * Remove a job from the manager
     * @param {string} uid - Job identifier
     */
    removeJob(uid) {
        if (!isFeatureEnabled('USE_NEW_JOB_MANAGER')) {
            return;
        }

        const job = this.jobs.get(uid);
        if (job) {
            this.jobs.delete(uid);
            console.log(`[JobManager] Removed job ${uid}`);
            this.notifyListeners('job_removed', uid, job);

            // Stop polling if no more jobs
            if (this.jobs.size === 0) {
                this.stopPolling();
            }
        }
    }

    /**
     * Cancel a job
     * @param {string} uid - Job identifier
     */
    cancelJob(uid) {
        if (!isFeatureEnabled('USE_NEW_JOB_MANAGER')) {
            return;
        }

        const job = this.jobs.get(uid);
        if (job) {
            this.updateJob(uid, { status: 'cancelling' });
            console.log(`[JobManager] Cancelled job ${uid}`);
        }
    }

    /**
     * Get all active jobs (generating or pending)
     * @returns {Array} Array of active job states
     */
    getActiveJobs() {
        return Array.from(this.jobs.values()).filter(job =>
            job.status === 'generating' || job.status === 'pending'
        );
    }

    /**
     * Get a specific job by UID
     * @param {string} uid - Job identifier
     * @returns {object|null} Job state or null if not found
     */
    getJob(uid) {
        return this.jobs.get(uid) || null;
    }

    /**
     * Get all jobs
     * @returns {Map} Map of all jobs
     */
    getAllJobs() {
        return new Map(this.jobs);
    }

    /**
     * Start the coordinated polling loop
     */
    startPolling() {
        if (!isFeatureEnabled('USE_NEW_JOB_MANAGER')) {
            return;
        }

        if (this.isPolling) {
            console.log('[JobManager] Polling already active');
            return;
        }

        this.isPolling = true;
        this.pollingInterval = setInterval(this.pollAllJobs, this.pollingIntervalMs);
        console.log(`[JobManager] Started polling (interval: ${this.pollingIntervalMs}ms)`);
    }

    /**
     * Stop the polling loop
     */
    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        this.isPolling = false;
        console.log('[JobManager] Stopped polling');
    }

    /**
     * Poll all active jobs in a coordinated manner
     */
    async pollAllJobs() {
        if (!isFeatureEnabled('USE_NEW_JOB_MANAGER')) {
            this.stopPolling();
            return;
        }

        const activeJobs = this.getActiveJobs();

        if (activeJobs.length === 0) {
            console.log('[JobManager] No active jobs, stopping polling');
            this.stopPolling();
            return;
        }

        console.log(`[JobManager] Polling ${activeJobs.length} active jobs`);

        // Poll jobs sequentially to avoid database contention
        for (const job of activeJobs) {
            try {
                // ✅ Defensive check: ensure job object is valid
                if (!job || !job.uid) {
                    console.warn(`[JobManager] Invalid job object encountered:`, job);
                    continue;
                }

                await this.pollSingleJob(job.uid);
            } catch (error) {
                console.error(`[JobManager] Error polling job ${job?.uid || 'unknown'}:`, error);
                if (job?.uid) {
                    this.handleJobError(job.uid, error);
                }
            }
        }
    }

    /**
     * Poll a single job by emitting event to existing system
     * @param {string} uid - Job identifier
     */
    async pollSingleJob(uid) {
        try {
            // ✅ Defensive check: ensure job still exists before polling
            const job = this.jobs.get(uid);
            if (!job) {
                console.warn(`[JobManager] Job ${uid} no longer exists in JobManager, skipping poll`);
                return;
            }

            // ✅ Defensive check: ensure eventBus is available
            const eventBus = getEventBus();
            if (!eventBus) {
                console.error(`[JobManager] EventBus not available for job ${uid}`);
                return;
            }

            // Emit event that the existing fetchJob function will handle
            // This allows us to use the centralized job manager while keeping existing poll logic
            eventBus.emit('poll_job', uid);
        } catch (error) {
            console.error(`[JobManager] Error in pollSingleJob for ${uid}:`, error);
            this.handleJobError(uid, error);
        }
    }

    /**
     * Handle job polling errors
     * @param {string} uid - Job identifier
     * @param {Error} error - The error that occurred
     */
    handleJobError(uid, error) {
        console.error(`[JobManager] Job ${uid} error:`, error);

        const job = this.jobs.get(uid);
        if (job) {
            this.updateJob(uid, {
                error: error.message,
                status: 'error',
                lastErrorTime: Date.now()
            });
        }
    }

    /**
     * Add a listener for job state changes
     * @param {Function} callback - Callback function (eventType, uid, jobState)
     */
    addListener(callback) {
        this.listeners.add(callback);
    }

    /**
     * Remove a listener for job state changes
     * @param {Function} callback - Callback function to remove
     */
    removeListener(callback) {
        this.listeners.delete(callback);
    }

    /**
     * Notify all listeners of job state changes
     * @param {string} eventType - Type of event (job_added, job_updated, etc.)
     * @param {string} uid - Job identifier
     * @param {object} jobState - Current job state
     */
    notifyListeners(eventType, uid, jobState) {
        this.listeners.forEach(callback => {
            try {
                callback(eventType, uid, jobState);
            } catch (error) {
                console.error('[JobManager] Listener error:', error);
            }
        });
    }

    /**
     * Clean up resources and stop polling
     */
    destroy() {
        this.stopPolling();
        this.jobs.clear();
        this.listeners.clear();

        // Remove event listeners if they were initialized
        if (this.listenersInitialized) {
            const eventBus = getEventBus();
            eventBus.off('job_add', this.addJob.bind(this));
            eventBus.off('job_update', this.updateJob.bind(this));
            eventBus.off('job_remove', this.removeJob.bind(this));
            eventBus.off('job_cancel', this.cancelJob.bind(this));
            this.listenersInitialized = false;
        }

        console.log('[JobManager] Destroyed');
    }

    /**
     * Get manager statistics for debugging
     * @returns {object} Statistics about the job manager
     */
    getStats() {
        const jobs = Array.from(this.jobs.values());
        return {
            totalJobs: jobs.length,
            activeJobs: this.getActiveJobs().length,
            completedJobs: jobs.filter(j => j.status === 'done').length,
            errorJobs: jobs.filter(j => j.status === 'error').length,
            isPolling: this.isPolling,
            pollingInterval: this.pollingIntervalMs
        };
    }
}

// Create singleton instance
const jobManager = new JobManager();

export default jobManager;